import React, {useEffect, useRef, useState} from 'react';
import {RouteComponentProps, useHistory} from 'react-router-dom';
import {
    IonButtons,
    IonHeader,
    IonPage, IonSearchbar, IonToast
} from '@ionic/react';
import {StyledDataTable, StyledIonContent, StyledIonTitle, StyledIonToolbar} from '@components/content/content.style';
import {StyledHeaderButton, StyledHeaderButtonImage} from '@components/button/button.style';
import ArrowLeftImage from '../../assets/images/arrow-left.svg';
import AvatarImage from '../../assets/images/logo/x-black.png';
import {StyledContent} from './choosePersonPage.style';
import StaticPane from '../../components/pane/static-pane.component';
import {useTranslation} from 'react-i18next';
import {getWorkerConfiguration} from '@services/qrCodePage.service';
import {ReportWorker} from "@models/raport";
import LoadingSkeleton from "../../components/loading/loadingSkeleton.component";
import {useIsInViewport} from "@utils/tools/viewport";
import {checkWorkerHasActiveSickLeave} from "@services/sickLeave.service";
import {orderWorkers as orderWorkersFactory} from "../../services/reportFactory.service";
import moment from "moment";

interface RouterProps {
    orderId: string;
    serviceDate: string;
}

interface ChoosePersonProps extends RouteComponentProps<RouterProps> {
}

const ChoosePersonPage: React.FC<ChoosePersonProps> = ({match}) => {
    const history = useHistory();
    const {t} = useTranslation();

    const [topEdge, updateTopEdge] = useState<number | undefined>(undefined);
    const [error, setError] = useState<string>();
    const [workers, updateWorkers] = useState<ReportWorker[]>();
    const [loading, setLoading] = useState<boolean>(false);
    const [reloading, setReloading] = useState<boolean>(false);
    const [page, setPage] = useState(1);
    const [searchText, setSearchText] = useState<string | undefined>();
    const loadMoreRef = useRef(null);
    const loadMoreIsInViewport = useIsInViewport(loadMoreRef);
    const [loadMoreDisabled, setLoadMoreDisabled] = useState(false);
    const [showToast, setShowToast] = useState<boolean>(false);
    const [toast, setToast] = useState<string>('');

    const {params: {orderId, serviceDate}} = match;

    const configuration = getWorkerConfiguration();

    useEffect(() => {
        updateHeight();
    });

    const handleBack = () => {
        history.goBack();
    };

    const choosePerson = async (worker: ReportWorker) => {
        const hasActiveSickLeave = await validateSickLeave(moment(serviceDate, 'DD-MM-YYYY').format('YYYY-MM-DD'), worker);

        if (hasActiveSickLeave) {
            return;
        }

        if (configuration?.handleScan) {
            let error = await configuration?.handleScan(worker.number);
            if (error === false) {
                setError(t('reportsTab.orders.scanPersonPage.error'))
            }
        }
    };

    const updateHeight = () => {
        if (header.current?.clientHeight == 0) {
            setTimeout(updateHeight);
        } else {
            updateTopEdge(header.current?.clientHeight);
        }
    };

    const header = useRef<HTMLIonHeaderElement>(null);

    if (!configuration) {
        history.goBack();
    }

    const fetchWorkers = async (forceReload: boolean = false, append: boolean = false, page: number = 1) => {
        if (!append) {
            setLoading(true)
        } else {
            setReloading(true);
        }
        if (workers === undefined || forceReload) {
            if (!append) {
                updateWorkers([]);
            }
            await orderWorkersFactory(orderId, serviceDate, page, searchText)
                .then(response => {
                    if (append) {
                        if (workers) {
                            let newData = response;
                            if (newData && newData.length == 0) {
                                setLoadMoreDisabled(true);
                            } else {
                                setLoadMoreDisabled(false);
                            }
                            updateWorkers([...workers, ...newData]);
                        }
                        setReloading(false);
                    } else {
                        setLoadMoreDisabled(false);
                        updateWorkers(response);
                    }

                    setLoading(false)
                }).catch(() => {
                    setLoading(false)
                    setReloading(false)
                });
        }
    }

    useEffect(() => {
        setPage(1);
        if (searchText !== undefined) {
            fetchWorkers(true, false, 1);
        }
    }, [searchText])

    const loadMoreWorkers = async () => {
        let tmp = page + 1;
        setPage(page + 1);
        fetchWorkers(true, true, tmp);
    };

    useEffect(() => {
        if (workers && workers.length > 0 && loadMoreIsInViewport && !loadMoreDisabled) {
            loadMoreWorkers();
        }
    }, [loadMoreIsInViewport]);

    useEffect(() => {
        fetchWorkers();
    }, []);

    const validateSickLeave = async (date: string, worker: ReportWorker) => {
        const hasActiveSickLeave = await checkWorkerHasActiveSickLeave(date, worker);

        if (hasActiveSickLeave) {
            setToast(t("reportPage.validation.hasActiveSickLeave"));
            setShowToast(true);

            return true;
        }

        return false;
    }

    return (
        <IonPage>
            <IonToast
                isOpen={showToast}
                onDidDismiss={() => {
                    setShowToast(false);
                    setToast('');
                }}
                message={toast}
                duration={6000}
                position="top"
                color="danger"
            />
            <StyledIonContent>
                <IonHeader ref={header} className="ion-no-border">
                    <StyledIonToolbar>
                        <IonButtons slot="start">
                            <StyledHeaderButton className="back-btn" onClick={() => handleBack()}>
                                <StyledHeaderButtonImage src={ArrowLeftImage}></StyledHeaderButtonImage>
                            </StyledHeaderButton>
                        </IonButtons>
                        <StyledIonTitle className="wrap-text">
                            <div className="wrap-text">
                                {t('choosePerson.title')}
                            </div>
                        </StyledIonTitle>
                        {/*<IonButtons slot="end">*/}
                        {/*    <StyledHeaderButton onClick={() => handleSave()}>*/}
                        {/*        <StyledHeaderButtonImage src={CheckImage}></StyledHeaderButtonImage>*/}
                        {/*    </StyledHeaderButton>*/}
                        {/*</IonButtons>*/}
                    </StyledIonToolbar>
                </IonHeader>
                <StaticPane topEdge={topEdge} marginTop={40} hideGrabber={true}>
                    <StyledContent>
                        <IonSearchbar
                            debounce={1000}
                            onIonChange={e => setSearchText(e.detail.value ?? '')}
                            onIonInput={e => setSearchText(e.detail.value ?? '')}
                            placeholder={t('choosePerson.search')}
                        />
                        <div className="content full-height">
                            {loading && <LoadingSkeleton includeAvatar={true} />}
                            {!loading && <StyledDataTable className="no-margin">
                                { workers && workers.map((data, i) => <div key={i} className="data-row" onClick={() => choosePerson(data)}>
                                    <div className="data-icon-title">
                                        <img className="avatar" src={data.user && (data.user?.photos || data.user?.avatar) ? (data.user.photos?.thumbnail ? data.user.photos.thumbnail : data.user.avatar) : AvatarImage}/>
                                        {data.name} ({data.number})
                                    </div>
                                </div>)
                                }
                            </StyledDataTable>}

                            {reloading && <LoadingSkeleton includeAvatar={true}/>}

                            <div ref={loadMoreRef}/>
                        </div>
                    </StyledContent>
                </StaticPane>
            </StyledIonContent>
        </IonPage>
    );
};

export default ChoosePersonPage;
